import { validTimeZone } from '@/shared/i18n/locale-settings'

/**
 * A search screen's state, kept in the page's URL so it can be bookmarked, shared and restored:
 * `q`, each `filter`, `sort`, `includeInactive`, and the output options `columns` and `tz`. Filters use the API's
 * syntax (`field:operator:value`, values separated by `|`), so most go to the API as they are.
 */

/** The operators the API takes, each with its negated form (`Search/FilterOperators.cs`). */
export type ApiOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'notStartsWith'
  | 'in'
  | 'notIn'
  | 'between'
  | 'notBetween'
  | 'isNull'
  | 'isNotNull'

/** A relative period, such as "in the last 7 days": sent to the API as an absolute UTC range. */
export type RelativeOperator = 'within' | 'notWithin'

export type FilterOperator = ApiOperator | RelativeOperator

/** One field filter. A range is `[from, to]`, either one empty; a period is `[direction, amount, unit]`. */
export interface FieldFilter {
  readonly field: string
  readonly operator: FilterOperator
  readonly values: readonly string[]
}

export interface SortState {
  readonly field: string
  readonly descending: boolean
}

export interface SearchState {
  /** Free text, searched across the screen's main fields. */
  readonly q: string
  readonly filters: readonly FieldFilter[]
  /** `null` for the screen's default order. */
  readonly sort: SortState | null
  /** The chosen columns in order, or `null` for the screen's defaults. */
  readonly columns: readonly string[] | null
  /** The IANA time zone dates are shown and exported in, or `null` for the viewer's own. */
  readonly timeZone: string | null
  /** Lists deactivated, disabled or archived records too; by default only active ones. */
  readonly includeInactive: boolean
}

export const emptySearch: SearchState = {
  q: '',
  filters: [],
  sort: null,
  columns: null,
  timeZone: null,
  includeInactive: false,
}

/** The URL's and the API's parameter for {@link SearchState.includeInactive}. */
export const includeInactiveParameter = 'includeInactive'

export type PeriodDirection = 'last' | 'next'
export type PeriodUnit = 'hours' | 'days' | 'weeks' | 'months' | 'years'

export const periodUnits: readonly PeriodUnit[] = ['hours', 'days', 'weeks', 'months', 'years']

const apiOperators: ReadonlySet<string> = new Set<ApiOperator>([
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'startsWith',
  'notStartsWith',
  'in',
  'notIn',
  'between',
  'notBetween',
  'isNull',
  'isNotNull',
])

const operators: ReadonlySet<string> = new Set([...apiOperators, 'within', 'notWithin'])

/** Whether the operator takes no value. */
export function takesNoValue(operator: FilterOperator): boolean {
  return operator === 'isNull' || operator === 'isNotNull'
}

/** Whether the operator is a negated form. */
export function isNegated(operator: FilterOperator): boolean {
  return operator.startsWith('not') || operator === 'isNotNull'
}

/** Whether the filter has what its operator needs, so it narrows the list. */
export function isComplete(filter: FieldFilter): boolean {
  const values = filter.values
  switch (filter.operator) {
    case 'isNull':
    case 'isNotNull':
      return true
    case 'between':
    case 'notBetween':
      return (values[0] ?? '') !== '' || (values[1] ?? '') !== ''
    case 'within':
    case 'notWithin':
      return isPeriod(values)
    default:
      return values.some((value) => value.trim() !== '')
  }
}

/** The most of each unit a period takes, so its range stays within the years 1 to 9999. */
export const periodLimits: Readonly<Record<PeriodUnit, number>> = {
  hours: 9999,
  days: 9999,
  weeks: 9999,
  months: 9999,
  years: 999,
}

function isPeriod(values: readonly string[]): boolean {
  const [direction, amount, unit] = values
  return (
    (direction === 'last' || direction === 'next') &&
    periodUnits.includes(unit as PeriodUnit) &&
    /^[1-9]\d{0,3}$/u.test(amount ?? '') &&
    Number(amount) <= periodLimits[unit as PeriodUnit]
  )
}

/** One filter as the URL and the API write it: `field:operator:value|value`. */
export function formatFilter(filter: FieldFilter): string {
  return takesNoValue(filter.operator)
    ? `${filter.field}:${filter.operator}`
    : `${filter.field}:${filter.operator}:${filter.values.join('|')}`
}

/** Reads one `filter` value; `null` when it is not one. */
export function parseFilter(text: string): FieldFilter | null {
  const first = text.indexOf(':')
  if (first <= 0) {
    return null
  }
  const second = text.indexOf(':', first + 1)
  const field = text.slice(0, first)
  const operator = second === -1 ? text.slice(first + 1) : text.slice(first + 1, second)
  const value = second === -1 ? '' : text.slice(second + 1)
  if (!operators.has(operator)) {
    return null
  }
  const typed = operator as FilterOperator
  // As the API reads them: `|` separates only a list's, a range's or a period's values.
  let values: readonly string[]
  switch (typed) {
    case 'isNull':
    case 'isNotNull':
      if (value !== '') {
        return null
      }
      values = []
      break
    case 'in':
    case 'notIn':
      values = value.split('|').filter((item) => item.trim() !== '')
      break
    case 'between':
    case 'notBetween':
    case 'within':
    case 'notWithin':
      values = value.split('|')
      if (values.length !== (typed === 'within' || typed === 'notWithin' ? 3 : 2)) {
        return null
      }
      break
    default:
      values = [value]
  }
  const filter = { field, operator: typed, values }
  return isComplete(filter) ? filter : null
}

/** Whether the text is a real calendar day, `YYYY-MM-DD`. */
export function isDay(text: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(text)) {
    return false
  }
  const [year = 0, month = 0, day = 0] = text.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

/** Reads the state from the page's URL. Anything malformed is left out. */
export function readSearchState(params: URLSearchParams): SearchState {
  const sort = params.get('sort') ?? ''
  const columns = params.get('columns')
  const timeZone = params.get('tz')

  return {
    q: params.get('q') ?? '',
    filters: params
      .getAll('filter')
      .map((value) => parseFilter(value))
      .filter((filter): filter is FieldFilter => filter !== null),
    sort:
      sort === '' || sort === '-'
        ? null
        : sort.startsWith('-')
          ? { field: sort.slice(1), descending: true }
          : { field: sort, descending: false },
    columns: chosenColumns(columns),
    timeZone: validTimeZone(timeZone),
    includeInactive: params.get(includeInactiveParameter) === 'true',
  }
}

/** The chosen columns, or `null` (the defaults) for none: a search always shows a column. */
function chosenColumns(text: string | null): readonly string[] | null {
  const columns = (text ?? '').split(',').filter((column) => column !== '')
  return columns.length === 0 ? null : columns
}

/** Writes the state as URL parameters, leaving out what is empty or the default. */
export function writeSearchState(state: SearchState): URLSearchParams {
  const params = new URLSearchParams()
  if (state.q.trim() !== '') {
    params.set('q', state.q)
  }
  for (const filter of state.filters) {
    if (isComplete(filter)) {
      params.append('filter', formatFilter(filter))
    }
  }
  if (state.sort !== null) {
    params.set('sort', sortParameter(state.sort))
  }
  if (state.columns !== null && state.columns.length > 0) {
    params.set('columns', state.columns.join(','))
  }
  if (state.timeZone !== null) {
    params.set('tz', state.timeZone)
  }
  if (state.includeInactive) {
    params.set(includeInactiveParameter, 'true')
  }
  return params
}

function sortParameter(sort: SortState): string {
  return `${sort.descending ? '-' : ''}${sort.field}`
}

/** How each filtered field is kept, so dates can be sent as instants. */
export type FieldKind = 'text' | 'person' | 'choice' | 'number' | 'date'

/**
 * The search's query for the API: `q`, each filter and `sort`, with a relative period turned into
 * an absolute UTC range from `now`, and a date range's days, read in `timeZone`, into the
 * instants from the start of the first day to the end of the last.
 */
export function toApiQuery(
  state: SearchState,
  kinds: Readonly<Record<string, FieldKind>>,
  timeZone: string,
  now: Date,
): URLSearchParams {
  const params = new URLSearchParams()
  if (state.q.trim() !== '') {
    params.set('q', state.q.trim())
  }
  for (const filter of state.filters) {
    if (isComplete(filter)) {
      params.append('filter', formatFilter(toApiFilter(filter, kinds[filter.field], timeZone, now)))
    }
  }
  if (state.sort !== null) {
    params.set('sort', sortParameter(state.sort))
  }
  if (state.includeInactive) {
    params.set(includeInactiveParameter, 'true')
  }
  return params
}

function toApiFilter(
  filter: FieldFilter,
  kind: FieldKind | undefined,
  timeZone: string,
  now: Date,
): FieldFilter {
  if (filter.operator === 'within' || filter.operator === 'notWithin') {
    const [direction, amount, unit] = filter.values
    const moved = shift(now, (direction === 'next' ? 1 : -1) * Number(amount), unit as PeriodUnit)
    const [from, to] = direction === 'next' ? [now, moved] : [moved, now]
    return {
      field: filter.field,
      operator: filter.operator === 'within' ? 'between' : 'notBetween',
      values: [from.toISOString(), to.toISOString()],
    }
  }

  if (kind === 'date' && (filter.operator === 'between' || filter.operator === 'notBetween')) {
    const [from = '', to = ''] = filter.values
    return {
      ...filter,
      values: [
        from === '' ? '' : startOfDay(from, timeZone).toISOString(),
        to === '' ? '' : endOfDay(to, timeZone),
      ],
    }
  }

  return filter
}

const hour = 3_600_000

function shift(from: Date, amount: number, unit: PeriodUnit): Date {
  switch (unit) {
    case 'hours':
      return new Date(from.getTime() + amount * hour)
    case 'days':
      return new Date(from.getTime() + amount * 24 * hour)
    case 'weeks':
      return new Date(from.getTime() + amount * 7 * 24 * hour)
    case 'months':
      return shiftMonths(from, amount)
    case 'years':
      return shiftMonths(from, amount * 12)
  }
}

/** `from` moved by whole months, its day kept, or the month's last day when it has fewer. */
function shiftMonths(from: Date, months: number): Date {
  const target = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + months, 1))
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
  target.setUTCDate(Math.min(from.getUTCDate(), lastDay))
  target.setUTCHours(from.getUTCHours(), from.getUTCMinutes(), from.getUTCSeconds(), from.getUTCMilliseconds())
  return target
}

/**
 * The last instant of a day, as the API's inclusive range needs it: 100 ns, the finest a stored
 * date holds, before the next day starts. A millisecond would leave the day's last fraction out.
 */
function endOfDay(calendarDay: string, timeZone: string): string {
  const lastSecond = new Date(startOfDay(nextDay(calendarDay), timeZone).getTime() - 1000)
  return lastSecond.toISOString().replace(/\.000Z$/u, '.9999999Z')
}

/** The day after an ISO date (`2026-10-31` gives `2026-11-01`). */
function nextDay(day: string): string {
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, date + 1)).toISOString().slice(0, 10)
}

const day = 24 * hour

/**
 * The first instant of a calendar day in an IANA time zone. Midnight under each offset the zone
 * has around the day is a candidate; the earliest that falls on the day wins. So where clocks jump
 * past midnight the day starts at the jump, and where they go back at midnight, when it comes again.
 */
export function startOfDay(calendarDay: string, timeZone: string): Date {
  const [year = 0, month = 1, date = 1] = calendarDay.split('-').map(Number)
  const asUtc = Date.UTC(year, month - 1, date)
  const candidates = [asUtc - day, asUtc, asUtc + day]
    .map((instant) => asUtc - offsetAt(instant, timeZone))
    .filter((instant) => localDay(instant, timeZone) === calendarDay)
  // A day the zone skipped, as Pacific/Apia skipped 2011-12-30: its range starts with the next one.
  return candidates.length === 0 ? startOfDay(nextDay(calendarDay), timeZone) : new Date(Math.min(...candidates))
}

/** The calendar day an instant falls on in a time zone. */
function localDay(instant: number, timeZone: string): string {
  return new Date(instant + offsetAt(instant, timeZone)).toISOString().slice(0, 10)
}

const offsetFormats = new Map<string, Intl.DateTimeFormat>()

/** How far the zone's wall clock is ahead of UTC at an instant, in ms. */
function offsetAt(instant: number, timeZone: string): number {
  let format = offsetFormats.get(timeZone)
  if (format === undefined) {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    })
    offsetFormats.set(timeZone, format)
  }
  const parts = Object.fromEntries(
    format.formatToParts(new Date(instant)).map((part) => [part.type, Number(part.value)]),
  )
  const wall = Date.UTC(
    parts['year'] ?? 0,
    (parts['month'] ?? 1) - 1,
    parts['day'] ?? 1,
    parts['hour'] ?? 0,
    parts['minute'] ?? 0,
    parts['second'] ?? 0,
  )
  return wall - (instant - (instant % 1000))
}
